// @flow
import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, intlShape } from 'react-intl';

import HamburgerNavComponent from '../side-nav/hamburger/HamburgerNavComponent';
import Logo from '../../common/icons/logo/LogoComponent';
import ChevronIcon from '../../common/icons/chevron/ChevronComponent';
import CartIcon from '../../common/icons/cart/CartComponent';
import UserIcon from '../../common/icons/user/UserComponent';
import Tooltip from '../../common/tooltip/TooltipComponent';
import Dropdown from '../../common/dropdown/DropdownComponent';

import { envUrl } from '../../services/environment/EnvironmentService';

import { canManageConsent } from '../../services/user-permission/UserPermissionService';
import { getFlag } from '../../data/NavigationItems';
import headerMessages from '../../../data/header';
import * as abstractWindow from '../../../abstraction/window';

import './Header.scss';

type Store = {
  id: number,
  storeCode: string,
  currencySymbol: string,
  currencyCode: string,
};

const defaultStore = {
  id: 1,
  storeCode: 'uk',
  currencyCode: 'GBP',
  currencySymbol: '£',
};

class HeaderComponent extends React.Component {
  constructor() {
    super();
    this.state = {
      accountDropdown: false,
      currencyDropdown: false,
      highlightAccount: false,
      highlightCurrency: false,
      logoError: false,
    };

    this.accountClick = this.toggleAccount.bind(this);
    this.currencyClick = this.toggleCurrency.bind(this);
    this.accountHoverOn = this.highlightAccount.bind(this, true);
    this.accountHoverOff = this.highlightAccount.bind(this, false);
    this.currencyHoverOn = this.highlightCurrency.bind(this, true);
    this.currencyHoverOff = this.highlightCurrency.bind(this, false);
    this.close = this.closeDropdowns.bind(this);
  }

  clickStore(e: Event, store: { id: number }): void {
    this.props.actions.switchWebsite(store);
    abstractWindow.location.assign(envUrl(`/localisation/switch-site/${store.id}`, '/m4b'));
    this.stopPropagation(e);
  }

  stopPropagation(e: Event): void {
    e.stopPropagation();
  }

  closeDropdowns(): void {
    this.setState({
      accountDropdown: false,
      currencyDropdown: false,
    });
  }

  highlightAccount(status: boolean): void {
    this.setState({
      highlightAccount: status,
    });
  }

  highlightCurrency(status: boolean): void {
    if (this.hasStores()) {
      this.setState({
        highlightCurrency: status,
      });
    }
  }

  toggleAccount(e: Event): void {
    this.setState({
      accountDropdown: !this.state.accountDropdown,
      currencyDropdown: false,
    });
    this.stopPropagation(e);
  }

  toggleCurrency(e: Event): void {
    this.setState({
      accountDropdown: false,
      currencyDropdown: !this.state.currencyDropdown,
    });
    this.stopPropagation(e);
  }

  componentDidMount(): void {
    window.addEventListener('click', this.close);
  }

  componentWillUnmount(): void {
    window.removeEventListener('click', this.close);
  }

  getUsername(): string {
    // Sets the name while config is loading. Cannot be set as a default prop as the reducer overwrites the whole "config"
    const { name } = this.props.config;
    if (name) {
      return name;
    }
    return <FormattedMessage id="app.header.menu.usernameFallback" defaultMessage="Account" />;
  }

  renderLogo(cobrandingEnabled: boolean, companyLogoFileId: string): Element {
    let brandingClass = '';
    let logoOrBranding;
    const logoImage = cobrandingEnabled ? companyLogoFileId : null;
    if (logoImage && !this.state.logoError) {
      logoOrBranding = <img alt="" src={logoImage} className="header-component__image" onError={this.imageError} />;
    } else {
      brandingClass = 'header-component__img--default';
      logoOrBranding = <Logo />;
    }
    return <div className={`header-component__img ${brandingClass}`}>{logoOrBranding}</div>;
  }

  imageError = (): void => {
    this.setState({
      logoError: true,
    });
  };

  currentStore(): Store {
    const allStores = this.props.config.availableStores;
    const { selectedStoreCode } = this.props.config;
    if (allStores.length === 0 || !selectedStoreCode) {
      return defaultStore;
    }
    return allStores.find((store) => store.storeCode === selectedStoreCode) || allStores[0];
  }

  renderStoreDropdownExtra(): Element | null {
    const stores = this.props.config.availableStores;
    if (stores.length > 1) {
      return (
        <span
          className={`header-component__arrow ${this.state.currencyDropdown ? 'header-component__arrow--active' : ''}`}
        >
          <ChevronIcon />
        </span>
      );
    }
    // Only 1 store allowed
    if (this.state.highlightCurrency) {
      const storeText = `${stores[0].currencySymbol}${stores[0].currencyCode}`;
      const msg = this.context.intl.formatMessage(headerMessages.lockedStore, { storeText });
      return <Tooltip label={msg} position="top" isActive />;
    }
    return null;
  }

  renderStoreItem(store: Store): Element {
    return (
      <li
        key={store.storeCode}
        className="header-component__item"
        data-qa-id={`store-option-${store.id}`}
        onClick={(e) => this.clickStore(e, store)}
        onMouseEnter={this.currencyHoverOn}
        onMouseLeave={this.currencyHoverOff}
      >
        <span className="header-component__icon header-component__icon--flag">{getFlag(store.storeCode)}</span>
        <span className="header-component__label">
          {store.currencySymbol}
          {store.currencyCode}
        </span>
      </li>
    );
  }

  hasStores(): boolean {
    return this.props.config.availableStores.length > 0;
  }

  renderStoreDropdown(currentStoreCode: string): Element {
    const stores = this.props.config.availableStores;
    return (
      <Dropdown
        active={this.state.currencyDropdown}
        position="bottom-left"
        arrowHighlight={this.state.highlightCurrency}
      >
        <ul className="header-component__list u-margin-0">
          {stores.map((store) => (store.storeCode === currentStoreCode ? '' : this.renderStoreItem(store)))}
        </ul>
      </Dropdown>
    );
  }

  renderStoreSelector(): Element {
    const currentStore = this.currentStore();
    return (
      <div className="header-component__currency" data-qa-id="store-switcher" onClick={this.currencyClick}>
        <div onMouseEnter={this.currencyHoverOn} onMouseLeave={this.currencyHoverOff}>
          <span
            className="header-component__icon header-component__icon--flag"
            data-qa-id="current-store"
            data-store-id={currentStore.id}
          >
            {getFlag(currentStore.storeCode)}
          </span>
          <span className="header-component__label header-component__mobile-hide">
            {currentStore.currencySymbol}
            {currentStore.currencyCode}
          </span>
        </div>
        {this.hasStores() && this.renderStoreDropdownExtra()}
        {this.hasStores() && this.renderStoreDropdown(currentStore.storeCode)}
      </div>
    );
  }

  renderCart(): Element {
    const cartCount = this.props.cart;
    const hasCartItems = cartCount > 0;
    const badgeClass = hasCartItems ? 'header-component__icon--badge' : '';
    const cartBadge = hasCartItems ? 'header-component__cart--badge' : '';
    return (
      <a href={this.props.link.cart}>
        <div className={`header-component__cart ${cartBadge}`}>
          <span className={`header-component__icon header-component__icon--cart ${badgeClass}`}>
            {hasCartItems && (
              <span className="header-component__badge" data-qa-id="cart-basket-count">
                {cartCount}
              </span>
            )}
            <CartIcon />
          </span>
          <span className="header-component__label header-component__mobile-hide">
            <FormattedMessage id="app.header.menu.cart" defaultMessage="Cart" />
          </span>
        </div>
      </a>
    );
  }

  renderAccount(): Element {
    return (
      <div className="header-component__account" onClick={this.accountClick} data-qa-id="popover-account-menu-button">
        <span className="header-component__icon header-component__icon--account">
          <UserIcon />
        </span>
        <span className="header-component__label header-component__mobile-hide">{this.getUsername()}</span>
        <span
          className={`header-component__arrow ${this.state.accountDropdown ? 'header-component__arrow--active' : ''}`}
        >
          <ChevronIcon />
        </span>
        <Dropdown
          active={this.state.accountDropdown}
          position="bottom-left"
          arrowHighlight={this.state.highlightAccount}
        >
          <ul className="header-component__list list-bare">
            <a href={this.props.link.account}>
              <li
                className="header-component__item u-color-dark"
                onMouseEnter={this.accountHoverOn}
                onMouseLeave={this.accountHoverOff}
              >
                <FormattedMessage id="app.header.menu.link.account" defaultMessage="Your account" />
              </li>
            </a>
            <a href={this.props.link.browse} target="_blank" rel="noopener noreferrer">
              <li className="header-component__item u-color-dark">
                <FormattedMessage id="app.header.menu.link.soho" defaultMessage="Browse MOO products" />
              </li>
            </a>
            {canManageConsent(this.props.config.permissions) && (
              <a href={this.props.link.consent} target="_blank" rel="noopener noreferrer">
                <li className="header-component__item u-color-dark">
                  <FormattedMessage id="app.header.menu.link.consent" defaultMessage="Manage your information" />
                </li>
              </a>
            )}
            <a href={this.props.link.logout}>
              <li className="header-component__item u-color-dark">
                <FormattedMessage id="app.header.menu.link.logout" defaultMessage="Logout" />
              </li>
            </a>
          </ul>
        </Dropdown>
      </div>
    );
  }

  render(): Element {
    return (
      <header className={`header-component u-display-flex u-flexDirection-row ${this.props.headerClass}`}>
        <HamburgerNavComponent active={this.props.menu} actions={this.props.actions} />
        {this.renderLogo(this.props.config.cobrandingEnabled, this.props.config.companyLogoFileId)}
        <div className="header-component__container">
          {this.renderStoreSelector()}
          {this.renderCart()}
          {this.renderAccount()}
        </div>
      </header>
    );
  }
}

const storeShape = PropTypes.shape({
  id: PropTypes.number,
  storeCode: PropTypes.string,
  currencyCode: PropTypes.string,
  currencySymbol: PropTypes.string,
});

HeaderComponent.displayName = 'Header';

HeaderComponent.propTypes = {
  headerClass: PropTypes.string,
  config: PropTypes.shape({
    name: PropTypes.string,
    cobrandingEnabled: PropTypes.bool,
    companyLogoFileId: PropTypes.string,
    selectedStoreCode: PropTypes.string.isRequired,
    availableStores: PropTypes.arrayOf(storeShape).isRequired,
    permissions: PropTypes.array.isRequired,
  }).isRequired,
  cart: PropTypes.number,
  link: PropTypes.shape({
    account: PropTypes.string,
    browse: PropTypes.string,
    consent: PropTypes.string,
    logout: PropTypes.string,
    cart: PropTypes.string,
  }),
  actions: PropTypes.shape({
    switchWebsite: PropTypes.func,
  }),
  menu: PropTypes.bool,
};

HeaderComponent.defaultProps = {
  headerClass: '',
  config: {
    cobrandingEnabled: false,
    companyLogoFileId: null,
  },
  cart: 0,
  link: {
    account: '#',
    browse: '#',
    consent: '#',
    logout: '#',
    cart: '#',
  },
};

HeaderComponent.contextTypes = {
  intl: intlShape.isRequired,
};

export default HeaderComponent;
